import React, { createContext, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import { clinicService, userService } from '../services';

const AppContext = createContext();

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within AppProvider');
    }
    return context;
};

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(true);
    const { i18n } = useTranslation();

    useEffect(() => {
        // Initialize app - load user and clinic data
        const initializeApp = async () => {
            try {
                if (authService.isAuthenticated()) {
                    const currentUser = authService.getCurrentUser();
                    setUser(currentUser);

                    // Don't override language here - let i18n's language detector load from localStorage
                    // The language is already set by i18n.js on app initialization
                    // Only the login flow updates language from backend preferredLanguage

                    // Skip clinic fetch for super_admin
                    if (currentUser.role !== 'super_admin') {
                        try {
                            const clinicData = await clinicService.getProfile();
                            setClinic(clinicData.data);
                        } catch (clinicErr) {
                            console.error('Failed to fetch clinic:', clinicErr);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to initialize app:', error);
                authService.logout();
            } finally {
                setLoading(false);
            }
        };

        initializeApp();
    }, [i18n]);

    const login = async (phone, password) => {
        const response = await authService.login(phone, password);
        if (response.success) {
            setUser(response.data.user);

            // Set language from user preference
            if (response.data.user.preferredLanguage) {
                i18n.changeLanguage(response.data.user.preferredLanguage);
            }

            // Skip clinic fetch for super_admin
            if (response.data.user.role !== 'super_admin') {
                try {
                    const clinicData = await clinicService.getProfile();
                    setClinic(clinicData.data);
                } catch (clinicErr) {
                    console.error('Failed to fetch clinic:', clinicErr);
                }
            }
        }
        return response;
    };

    const logout = () => {
        setUser(null);
        setClinic(null);
        authService.logout();
    };

    const updateClinic = (updatedClinic) => {
        setClinic(updatedClinic);
    };

    const changeLanguage = async (language) => {
        try {
            // Update i18n language
            await i18n.changeLanguage(language);

            // Update user preference in backend if user is logged in
            if (user) {
                await userService.updateLanguage(language);
                // Update user state with new language preference
                const updatedUser = { ...user, preferredLanguage: language };
                setUser(updatedUser);
                // Persist to localStorage so language survives page refresh
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
        } catch (error) {
            console.error('Failed to change language:', error);
        }
    };

    const hasPermission = (permission) => {
        return user?.permissions?.includes(permission) || false;
    };

    const isAdmin = () => {
        return user?.role === 'admin';
    };

    const value = {
        user,
        clinic,
        loading,
        login,
        logout,
        updateClinic,
        changeLanguage,
        hasPermission,
        isAdmin,
        currentLanguage: i18n.language
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
